/**
 * The connection test result: a numbered list of checks the edge ran against one instrument, each with a fix when it
 * failed. Shared by Admin > Instruments and the instrument detail page.
 */
import { ReactNode, useEffect, useState } from "react";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Aperture, CaretUp, CheckCircle, MinusCircle, Planet, WarningCircle, Wrench, XCircle } from "@phosphor-icons/react";
import { DiagnosticStep, NodeDiagnostic } from "../api";
import { ConnectionDots, Mono } from "../ui";
import { caps, tones } from "../theme";
import { ago } from "./common";

/** A connection test started from this page: POST /admin/nodes/:id/diagnose, then poll until a newer result lands. */
export interface DiagRun {
  phase: "running" | "done" | "timeout" | "unavailable" | "error";
  requestedAt: string;
  started: number;
  result?: NodeDiagnostic;
  message?: string;
}

export const DIAG_POLL_MS = 2000;
export const DIAG_MAX_MS = 60_000;
export const newer = (d: NodeDiagnostic | null | undefined, than: string | null | undefined) => !!d && (!than || new Date(d.at).getTime() > new Date(than).getTime());

const stepLook: Record<DiagnosticStep["status"], { color: string; icon: React.ReactNode; word: string }> = {
  pass: { color: tones.ok, icon: <CheckCircle size={20} weight="duotone" />, word: "passed" },
  warn: { color: tones.warn, icon: <WarningCircle size={20} weight="duotone" />, word: "warning" },
  fail: { color: tones.fail, icon: <XCircle size={20} weight="duotone" />, word: "failed" },
  skip: { color: "rgba(236,233,228,0.4)", icon: <MinusCircle size={20} />, word: "skipped" },
};

function streamFacts(st: NonNullable<NodeDiagnostic["stream"]>) {
  const out: { k: string; v: string }[] = [];
  if (st.codec) out.push({ k: "Codec", v: st.codec });
  if (st.width && st.height) out.push({ k: "Resolution", v: `${st.width} x ${st.height}` });
  if (st.fps) out.push({ k: "Frame rate", v: `${Math.round(st.fps * 10) / 10} fps` });
  if (st.bitrateKbps) out.push({ k: "Bitrate", v: st.bitrateKbps >= 1000 ? `${(st.bitrateKbps / 1000).toFixed(1)} Mbps` : `${Math.round(st.bitrateKbps)} kbps` });
  return out;
}

export function DiagnosticPanel({ aet, run, last, onHide, footer }: { aet: string; run?: DiagRun; last: NodeDiagnostic | null; onHide?: () => void; footer?: ReactNode }) {
  const [now, setNow] = useState(Date.now());
  const running = run?.phase === "running";
  useEffect(() => {
    if (!running) return;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [running]);
  const d = run?.result ?? (run && run.phase !== "timeout" ? null : last);
  return (
    <Box sx={{ border: 1, borderColor: "divider", borderRadius: "14px", bgcolor: "rgba(0,0,0,0.28)", p: { xs: 2, md: 2.5 } }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: running || d ? 2 : 0 }}>
        <Typography variant="overline" sx={{ color: "primary.main", lineHeight: 1.6 }}>Connection test</Typography>
        <Mono sx={{ color: "text.secondary" }}>{aet}</Mono>
        <Box sx={{ flexGrow: 1 }} />
        {onHide && <Button size="small" color="inherit" endIcon={<CaretUp size={12} />} onClick={onHide} sx={{ color: "text.secondary" }}>Hide</Button>}
      </Stack>
      {running && (
        <Stack spacing={1.25}>
          <ConnectionDots state="connecting" from={<Planet size={20} />} to={<Aperture size={20} />} dots={10} size={5} label={`Asking the Pandia to test the instrument. ${Math.max(0, Math.round((now - run!.started) / 1000))}s elapsed`} />
          <Typography variant="caption" color="text.secondary">The Pandia runs the checks on site and reports the result. This usually takes a few seconds.</Typography>
        </Stack>
      )}
      {run?.phase === "unavailable" && <Alert severity="info">The connection test is not available on this server yet.</Alert>}
      {run?.phase === "error" && <Alert severity="error">{run.message}</Alert>}
      {run?.phase === "timeout" && (
        <Alert severity="warning" sx={{ mb: d ? 2 : 0 }}>
          No result after {Math.round(DIAG_MAX_MS / 1000)} s. The edge may be offline or busy. The result appears here when the edge reports.{d ? " The previous result is shown below." : ""}
        </Alert>
      )}
      {d && (
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ display: "flex", color: d.ok ? tones.ok : tones.fail }}>{d.ok ? <CheckCircle size={26} weight="duotone" /> : <XCircle size={26} weight="duotone" />}</Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 500 }}>{d.summary}</Typography>
              <Typography variant="caption" color="text.secondary">{new Date(d.at).toLocaleString()} · {ago(d.at)}</Typography>
            </Box>
          </Stack>
          <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0 }}>
            {d.steps.map((st, i) => {
              const look = stepLook[st.status] ?? stepLook.skip;
              const lastStep = i === d.steps.length - 1;
              return (
                <Box component="li" key={st.key} sx={{ display: "flex", gap: 1.5, position: "relative", pb: lastStep ? 0 : 1.75 }}>
                  {!lastStep && <Box aria-hidden sx={{ position: "absolute", left: 9.5, top: 24, bottom: 2, width: "1px", bgcolor: "divider" }} />}
                  <Box sx={{ color: look.color, display: "flex", pt: "1px", flexShrink: 0 }} aria-label={look.word}>{look.icon}</Box>
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
                      <Typography sx={{ fontWeight: 500, color: st.status === "skip" ? "text.secondary" : "text.primary" }}>{st.label}</Typography>
                      <Typography sx={{ ...caps, fontSize: 9, color: look.color }}>{look.word}</Typography>
                    </Stack>
                    {st.detail && <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>{st.detail}</Typography>}
                    {st.fix && (st.status === "fail" || st.status === "warn") && (
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ mt: 1, p: 1.25, borderRadius: "10px", borderLeft: `2px solid ${look.color}`, bgcolor: alpha(look.color, 0.08), color: "text.primary" }}
                      >
                        <Box sx={{ color: look.color, display: "flex", pt: "2px" }}><Wrench size={16} /></Box>
                        <Typography variant="body2"><Box component="span" sx={{ ...caps, fontSize: 10, color: look.color, mr: 1 }}>Fix</Box>{st.fix}</Typography>
                      </Stack>
                    )}
                    {st.fix && st.status !== "fail" && st.status !== "warn" && <Typography variant="caption" color="text.secondary">{st.fix}</Typography>}
                    {/* What the device actually said (SDP, reply headers, packets received): everything, no log digging */}
                    {st.data && Object.keys(st.data).some((k) => k !== "fingerprint") && (
                      <Box component="details" sx={{ mt: 0.75, "& summary": { cursor: "pointer", color: "text.secondary", fontSize: 12 } }}>
                        <summary>Device details</summary>
                        <Box component="pre" sx={{ m: 0, mt: 0.75, p: 1.25, borderRadius: "8px", bgcolor: "rgba(0,0,0,0.3)", fontSize: 11, whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "monospace" }}>
                          {Object.entries(st.data).map(([k, v]) => `${k}:\n${v}`).join("\n\n")}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
          {d.stream && streamFacts(d.stream).length > 0 && (
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ display: "block", lineHeight: 1.6, mb: 0.75 }}>Live stream</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {streamFacts(d.stream).map((f) => (
                  <Box key={f.k} sx={{ px: 1.25, py: 0.75, borderRadius: "10px", border: 1, borderColor: "divider", bgcolor: "rgba(255,255,255,0.03)" }}>
                    <Typography sx={{ ...caps, fontSize: 9, color: "text.secondary" }}>{f.k}</Typography>
                    <Mono sx={{ fontSize: 13, color: "secondary.light" }}>{f.v}</Mono>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      )}
      {footer && <Box sx={{ mt: 2 }}>{footer}</Box>}
    </Box>
  );
}

